import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useChannelStore, useChannelMemberStore, useDmStore, usePresenceStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { useCan } from '../hooks/usePermissions';
import { routes } from '../lib/routes';
import { useGuildId } from '../hooks/useGuildId';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import ChannelList from './ChannelList';
import { PlusOutlined } from '@ant-design/icons';
import type { DmChannel } from '../types';
import type { AgentRuntimeReason, AgentRuntimeState, ChannelMember } from '../lib/api';
import PresenceDot from './PresenceDot';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';
import { usePresence } from '../hooks/usePresence';

const EMPTY_MEMBERS: ChannelMember[] = [];

export default function Sidebar({ serverOnly = false, onTaskClick, activeThreadId, activeChannelId }: { serverOnly?: boolean; onTaskClick?: (threadId: string) => void; activeThreadId?: string; activeChannelId?: string | null }) {
  const channels = useChannelStore(s => s.channels);
  const currentChannelId = useChannelStore(s => s.currentChannelId);
  const channelMembersVersion = useChannelStore(s => s.channelMembersVersion[s.currentChannelId ?? ''] ?? 0);
  const createChannel = useChannelStore(s => s.createChannel);
  const dmChannels = useDmStore(s => s.dmChannels);
  const loadDmChannels = useDmStore(s => s.loadDmChannels);
  const openDm = useDmStore(s => s.openDm);
  const currentUser = useAuthStore(s => s.currentUser);
  const onlineUserIds = usePresenceStore(s => s.onlineUserIds);
  const tasksById = useTaskStore(s => s.byTaskId);
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useGuildId();
  const isDmMode = !serverOnly && location.pathname.startsWith('/dm');
  const canCreateChannel = useCan('channel.create');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const channelMembers = useChannelMemberStore(s => currentChannelId
    ? (s.byChannelId[currentChannelId] ?? EMPTY_MEMBERS)
    : EMPTY_MEMBERS);
  const loadChannelMembers = useChannelMemberStore(s => s.loadChannelMembers);

  useEffect(() => {
    if (!currentChannelId) return;
    void loadChannelMembers(currentChannelId, channelMembersVersion).catch(() => {});
  }, [currentChannelId, channelMembersVersion, loadChannelMembers]);

  useEffect(() => {
    loadDmChannels();
  }, [loadDmChannels]);

  useEffect(() => {
    if (!isDmMode && guildId) void useTaskStore.getState().loadGuildTasks(guildId).catch(() => {});
  }, [guildId, isDmMode]);

  const handleSelect = (channelId: string) => {
    if (!runUnsavedGuards()) return;
    navigate(routes.channel(guildId, channelId));
  };

  const handleSelectDm = (dmChannelId: string) => {
    if (!runUnsavedGuards()) return;
    navigate(routes.dm(dmChannelId));
  };

  const toggleMember = (userId: string) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const channel = await createChannel(
        newName.trim(),
        newTopic.trim() || undefined,
        visibility === 'private' && selectedMemberIds.size > 0 ? [...selectedMemberIds] : undefined,
        visibility,
      );
      navigate(routes.channel(guildId, channel.id));
      setNewName('');
      setNewTopic('');
      setSelectedMemberIds(new Set());
      setVisibility('public');
      setShowCreate(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };


  const nonDmChannels = channels.filter(c => c.type !== 'dm');
  const activeTasks = useMemo(
    () => Object.values(tasksById).filter(task =>
      task.guild_id === guildId &&
      (task.status === 'in_progress' || task.status === 'in_review') &&
      nonDmChannels.some(channel => channel.id === task.channel_id && channel.is_member !== false),
    ),
    [guildId, nonDmChannels, tasksById],
  );

  const sortedDms = [...dmChannels].sort((a, b) => {
    const aTime = a.last_message?.created_at ?? a.created_at;
    const bTime = b.last_message?.created_at ?? b.created_at;
    return bTime - aTime;
  });

  return (
    <div className="sidebar">
      {isDmMode ? (
        <>
          <div className="sidebar-header">
            <span className="sidebar-title">Direct Messages</span>
          </div>
          <MergedDmList
            dms={sortedDms}
            currentChannelId={activeChannelId ?? null}
            onlineUserIds={onlineUserIds}
            users={channelMembers}
            currentUserId={currentUser?.id}
            onSelectDm={handleSelectDm}
            onOpenDm={async (userId: string) => {
              const dm = await openDm(userId);
              navigate(routes.dm(dm.id));
            }}
          />
        </>
      ) : (
        <>
          <div className={`sidebar-header${serverOnly ? ' sidebar-header-section' : ''}`}>
            <span className="sidebar-title">Channels</span>
            <div className="sidebar-actions">
              {canCreateChannel && (
                <button
                  className="icon-btn"
                  onClick={() => setShowCreate(true)}
                  title="Create Channel"
                >
                  <PlusOutlined />
                </button>
              )}
            </div>
          </div>

          {showCreate && (
        <form className="channel-create-form" onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="Channel name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            autoFocus
            className="input-field"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newTopic}
            onChange={e => setNewTopic(e.target.value)}
            className="input-field"
          />
          <div className="visibility-toggle">
            <label className="visibility-option">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={() => setVisibility('public')}
              />
              <span>Public — visible to everyone</span>
            </label>
            <label className="visibility-option">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
              />
              <span>Private — invite only</span>
            </label>
          </div>
          {visibility === 'private' && (
          <div className="member-select-list">
            <div className="member-select-label">Select members (optional)</div>
            {channelMembers
              .filter(u => u.user_id !== currentUser?.id)
              .map(u => (
                <label key={u.user_id} className="member-select-item">
                  <input
                    type="checkbox"
                    checked={selectedMemberIds.has(u.user_id)}
                    onChange={() => toggleMember(u.user_id)}
                  />
                  <span><UserName displayName={u.display_name} role={u.role} /></span>
                </label>
              ))}
          </div>
          )}
          <div className="form-actions">
            <button type="submit" disabled={creating || !newName.trim()} className="btn btn-primary btn-sm">
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setSelectedMemberIds(new Set()); setVisibility('public'); }} className="btn btn-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      <ChannelList
        channels={nonDmChannels}
        currentChannelId={activeChannelId ?? null}
        activeTasks={activeTasks}
        activeThreadId={activeThreadId}
        onSelectChannel={handleSelect}
        onSelectTask={task => {
          if (task.thread_id) onTaskClick?.(task.thread_id);
        }}
        onShowAllTasks={channelId => navigate(`${routes.tasks(guildId)}?channel=${encodeURIComponent(channelId)}`)}
      />
        </>
      )}
    </div>
  );
}

function DmItem({ dm, active, online, onClick }: { dm: DmChannel; active: boolean; online: boolean; onClick: () => void }) {
  const peerName = dm.peer?.display_name ?? dm.name ?? 'DM';
  const isAgent = dm.peer?.role === 'agent';

  return (
    <button
      className={`channel-item ${active ? 'channel-item-active' : ''}`}
      onClick={onClick}
      data-role={dm.peer?.role ?? 'user'}
    >
      <UserAvatar userId={dm.peer?.id ?? dm.id} displayName={peerName} size={28} />
      <span className="channel-name"><UserName displayName={peerName} role={dm.peer?.role} /></span>
      {isAgent && dm.peer?.id && (
        <DmPresence
          agentID={dm.peer.id}
          online={online}
          state={dm.peer.state}
          reason={dm.peer.reason}
        />
      )}
      {dm.unread_count > 0 && (
        <span className="unread-badge">{dm.unread_count > 99 ? '99+' : dm.unread_count}</span>
      )}
    </button>
  );
}

function DmPresence({
  agentID,
  online,
  state: stateProp,
  reason: reasonProp,
}: {
  agentID: string;
  online: boolean;
  state?: AgentRuntimeState;
  reason?: AgentRuntimeReason;
}) {
  const live = usePresence(agentID);
  const state = live?.state ?? stateProp ?? (online ? 'online' : undefined);
  const reason = live?.reason ?? (state === 'error' ? reasonProp : undefined);
  return <PresenceDot state={state} reason={reason} compact />;
}

function MergedDmList({ dms, currentChannelId, onlineUserIds, users, currentUserId, onSelectDm, onOpenDm }: {
  dms: DmChannel[];
  currentChannelId: string | null;
  onlineUserIds: Record<string, boolean>;
  users: ChannelMember[];
  currentUserId?: string;
  onSelectDm: (id: string) => void;
  onOpenDm: (userId: string) => void;
}) {
  const validDms = dms.filter(dm => dm.peer?.id);
  const dmPeerIds = new Set(validDms.map(dm => dm.peer.id));
  const availableMembers = users.filter(u => u.user_id !== currentUserId && !dmPeerIds.has(u.user_id));

  if (validDms.length === 0 && availableMembers.length === 0) return null;

  return (
    <div className="dm-list" data-kind="dm">
      <div className="online-header" data-kind="dm">Direct Messages</div>
      {validDms.map(dm => (
        <DmItem
          key={dm.id}
          dm={dm}
          active={dm.id === currentChannelId}
          online={!!onlineUserIds[dm.peer.id]}
          onClick={() => onSelectDm(dm.id)}
        />
      ))}
      {availableMembers.map(user => (
        <button
          key={user.user_id}
          className="channel-item online-only-item"
          onClick={() => onOpenDm(user.user_id)}
          title={`Message ${user.display_name}`}
        >
          <UserAvatar userId={user.user_id} displayName={user.display_name} size={28} />
          <span className="channel-name"><UserName displayName={user.display_name} role={user.role} /></span>
        </button>
      ))}
    </div>
  );
}
